/**
 * Path-based routing for the Collect web app.
 *
 * The app is a single SPA served from collect.qodoonect.com (Vercel project
 * `web`). vercel.json rewrites every non-asset path to /index.html, so a deep
 * link like /login?from=portal or /capsules/42 reaches this parser.
 *
 * Entry links that already exist in the repo:
 *   - HIVE Portal:   /login?from=portal                (hive-portal/src/data/stages.ts)
 *   - HIVE runes:    /login?from=runes&template=<slug> (landing-hive/runes/_collect-cta.js)
 *   - Rune handoff:  /send?from=runes&template=<slug>&handoff=1 — opened by a rune after the
 *                    visitor confirmed "Siųsti į Collect"; the rune's data arrives by
 *                    postMessage (lib/runeHandoff.ts), never in the URL
 *
 * `/` is the Awakening landing for signed-out visitors (D-1 A); `/auth/callback`
 * is where Trust sends the browser back after "Sign in with Ordinal Pass".
 *
 * Pure module (no DOM, no React) so it can be unit-tested with `node --test`.
 */

export type View =
  | 'root'
  | 'login'
  | 'auth-callback'
  | 'capsules'
  | 'capsule'
  | 'send'
  | 'settings'
  | 'terminal'
  | 'not-found';

export interface AppLocation {
  view: View;
  /** /capsules/:id */
  capsuleId?: number;
  /** Where the visitor came from (?from=portal | runes | …). Display only. */
  from?: string;
  /** Rune template slug (?template=Snake) — preselected in the send form. */
  template?: string;
  /**
   * ?handoff=1 with a template: a rune window opened this page and posts its export
   * (lib/runeHandoff.ts). Kept through /login and the Ordinal Pass round trip.
   */
  handoff?: true;
}

const SLUG_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;
const FROM_RE = /^[a-z0-9][a-z0-9_-]{0,31}$/;

/**
 * Rune template slug, or undefined when missing or malformed. "unknown" is the
 * rune button's fallback when it cannot derive a slug (_collect-cta.js), so it
 * means "no template".
 */
export function sanitizeTemplate(v: string | null | undefined): string | undefined {
  if (!v) return undefined;
  const t = v.trim();
  if (t.toLowerCase() === 'unknown') return undefined;
  return SLUG_RE.test(t) ? t : undefined;
}

/** `from` marker (lower-cased), or undefined when missing or malformed. */
export function sanitizeFrom(v: string | null | undefined): string | undefined {
  if (!v) return undefined;
  const f = v.trim().toLowerCase();
  return FROM_RE.test(f) ? f : undefined;
}

function normalizePath(pathname: string): string {
  let p = pathname || '/';
  // Tauri / static hosts may load /index.html directly
  p = p.replace(/\/index\.html$/i, '/');
  // Drop trailing slashes (but keep the root)
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return p || '/';
}

export function parseLocation(pathname: string, search: string): AppLocation {
  const params = new URLSearchParams(search || '');
  const from = sanitizeFrom(params.get('from'));
  const template = sanitizeTemplate(params.get('template'));
  // A handoff without a template is not one the rune button makes: ignored.
  const handoff = template && params.get('handoff') === '1' ? (true as const) : undefined;
  const extras = { ...(from ? { from } : {}), ...(template ? { template } : {}), ...(handoff ? { handoff } : {}) };

  const path = normalizePath(pathname);
  const parts = path.split('/').filter(Boolean);

  if (parts.length === 0) return { view: 'root', ...extras };

  const [head, second, ...rest] = parts;
  if (rest.length > 0) return { view: 'not-found' };

  switch (head) {
    case 'login':
      return second ? { view: 'not-found' } : { view: 'login', ...extras };
    case 'auth':
      return second === 'callback' ? { view: 'auth-callback' } : { view: 'not-found' };
    case 'send':
      return second ? { view: 'not-found' } : { view: 'send', ...extras };
    case 'settings':
      return second ? { view: 'not-found' } : { view: 'settings' };
    case 'terminal':
      return second ? { view: 'not-found' } : { view: 'terminal' };
    case 'capsules': {
      if (!second) return { view: 'capsules' };
      if (!/^\d{1,12}$/.test(second)) return { view: 'not-found' };
      const id = Number(second);
      return id > 0 ? { view: 'capsule', capsuleId: id } : { view: 'not-found' };
    }
    default:
      return { view: 'not-found' };
  }
}

export function buildPath(loc: AppLocation): string {
  const qs = new URLSearchParams();
  if ((loc.view === 'login' || loc.view === 'send' || loc.view === 'root') && loc.from) qs.set('from', loc.from);
  if ((loc.view === 'login' || loc.view === 'send' || loc.view === 'root') && loc.template) qs.set('template', loc.template);
  if ((loc.view === 'login' || loc.view === 'send' || loc.view === 'root') && loc.template && loc.handoff) qs.set('handoff', '1');
  const query = qs.toString();
  const suffix = query ? `?${query}` : '';

  switch (loc.view) {
    case 'root': return `/${suffix}`;
    case 'login': return `/login${suffix}`;
    case 'auth-callback': return '/auth/callback';
    case 'send': return `/send${suffix}`;
    case 'capsules': return '/capsules';
    case 'capsule': return loc.capsuleId ? `/capsules/${loc.capsuleId}` : '/capsules';
    case 'settings': return '/settings';
    case 'terminal': return '/terminal';
    case 'not-found': return '/';
  }
}

/** Where a signed-in visitor lands after /login: the send form when a rune template was given. */
export function afterLogin(loc: AppLocation): AppLocation {
  if (loc.template) {
    return { view: 'send', template: loc.template, ...(loc.from ? { from: loc.from } : {}), ...(loc.handoff ? { handoff: true as const } : {}) };
  }
  return { view: 'capsules' };
}

/**
 * Title of the signed-out landing at `/`. index.html carries the same string in <title>
 * (search engines and link previews read it before any script runs;
 * tests/landing-copy.test.mjs checks that they match).
 */
export const LANDING_TITLE = 'Collect — Breathe Life Into Information';

const SCREEN_TITLES: Record<Exclude<View, 'root'>, string> = {
  login: 'Sign in',
  'auth-callback': 'Signing in',
  capsules: 'Capsules',
  capsule: 'Capsule',
  send: 'Send a capsule',
  settings: 'Settings',
  terminal: 'Terminal',
  'not-found': 'Not found',
};

/** document.title of a screen: the landing title on signed-out `/`, else "<Screen> — Collect". */
export function documentTitle(loc: AppLocation, landing: boolean): string {
  if (loc.view === 'root') return landing ? LANDING_TITLE : 'Collect';
  const screen = loc.view === 'capsule' && loc.capsuleId ? `Capsule #${loc.capsuleId}` : SCREEN_TITLES[loc.view];
  return `${screen} — Collect`;
}

/**
 * Redirect for the current location, or null to render it as is.
 *   /        → /capsules when credentials exist; signed out it is the landing,
 *              except a link that carries a rune template goes on to /login
 *   /login   → afterLogin() when credentials already exist
 */
export function resolveRedirect(loc: AppLocation, hasCredentials: boolean): AppLocation | null {
  if (loc.view === 'root') {
    if (hasCredentials) return afterLogin(loc);
    return loc.template ? { ...loc, view: 'login' } : null;
  }
  if (loc.view === 'login' && hasCredentials) {
    return afterLogin(loc);
  }
  return null;
}
